import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import TranslationWrapper, { getTranslation } from "../TranslationWrapper/TranslationWrapper";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  Modal,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useWatch } from "react-hook-form";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import {
  CustomizableComponent,
  CustomizableComponentConfiguration,
} from "../../views/Vestigingen/Tabs/V5/CustomComponents";
import { JamezzStylesComponent } from "../../views/Vestigingen/Tabs/V5/JamezzStyles/JamezzStylesComponent";
import { Settings, Smartphone, Tablet } from "@mui/icons-material";
import { DesignSuggestion } from "../../views/Vestigingen/Tabs/V5/JamezzStyles/AllDesignSuggestions";
import { parseJSClassName } from "../../views/Vestigingen/Tabs/V5/JamezzStyles/JSClasses";
import { DesignSuggestionForm } from "../../views/Vestigingen/Tabs/V5/JamezzStyles/LiveInterface/DesignSuggestionForm";
import {
  HoverCard,
  HoverCardPositions,
  JsClasses,
  postMessageToIframe,
  useStyles,
} from "../../views/Vestigingen/Tabs/V5/JamezzStyles/LiveInterface/HoverCard";

export const JamezzStylesContext = createContext<{
  dynamicallyDefinedComponents: CustomizableComponentConfiguration;
  setDynamicallyDefinedComponents: Dispatch<SetStateAction<CustomizableComponentConfiguration>>;
}>({
  dynamicallyDefinedComponents: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setDynamicallyDefinedComponents: () => {},
});

export type JSClassParameter = {
  parameterName: "id" | "ApiId" | "ArticleGroupId" | "ArticleGroupApiId";
  value: number;
};

type LiveInterfaceProps = {
  defaultComponents: CustomizableComponent[];
  designSuggestions: DesignSuggestion[];
  salesareaType: "KIOSK" | "QR";
  showAdminValues: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export default function LiveInterfaceEnvironment(liveInterfaceProps: LiveInterfaceProps) {
  const [dynamicallyDefinedComponents, setDynamicallyDefinedComponents] = useState<CustomizableComponentConfiguration>(
    []
  );
  return (
    <JamezzStylesContext.Provider
      value={{
        dynamicallyDefinedComponents,
        setDynamicallyDefinedComponents,
      }}
    >
      <_LiveInterfaceEnvironment {...liveInterfaceProps} />
    </JamezzStylesContext.Provider>
  );
}

// These are the logical resolutions, not actual resolutions
const phoneSizes = {
  // iPhone 13 mini
  "mobile-small": { height: 812, width: 375 },
  // Samsung Galaxy S22 Ultra
  "mobile-large": { height: 854, width: 384 },
  // iPad 10 size/2
  tablet: { height: 820, width: 590 },
};

/**
 * FUNCTION CustomComponent
 * @param {*} props
 * @returns
 */
function _LiveInterfaceEnvironment({
  defaultComponents,
  showAdminValues,
  salesareaType,
  onOpen,
  onClose,
  designSuggestions,
}: LiveInterfaceProps) {
  const { classes } = useStyles();
  const { dynamicallyDefinedComponents } = useContext(JamezzStylesContext);
  const components = useMemo(
    () => defaultComponents.concat(dynamicallyDefinedComponents),
    [defaultComponents, dynamicallyDefinedComponents]
  );
  const [hoverCardHidden, setHoverCardHidden] = useState(true);
  const [editMode, setEditMode] = useState<"simple" | "advanced">("simple");

  designSuggestions = useMemo(() => {
    const simplifiedSalesarea = salesareaType === "KIOSK" ? "KIOSK" : "QR";
    return designSuggestions.filter((designSuggestion) => designSuggestion.for === simplifiedSalesarea);
  }, [salesareaType]);

  const [hoverCard, setHoverCard] = useState<HoverCardPositions>({
    hoveredValues: ["none"],
    hoveredAvailableParameters: [],
    hoveredWidth: 0,
    hoveredHeight: 0,
    mouseY: 0,
    hoveredLeftSpacing: 0,
    hoveredTopSpacing: 0,
  });

  //Live interface info:
  const [liveInterfaceIsLoading, setLiveInterfaceIsLoading] = useState(false);
  const [showLiveInterface, setShowLiveInterface] = useState(false);
  const [qrScreenSize, setQrScreenSize] = useState<"mobile-small" | "mobile-large" | "tablet">("mobile-small");
  const [portraitOrLandscape, setPortraitOrLandscape] = useState<"portrait" | "landscape">("portrait");

  useEffect(() => {
    if (showLiveInterface) {
      onOpen();
    } else {
      onClose();
    }
  }, [showLiveInterface]);

  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedClassNames, setSelectedClassNames] = useState<string[]>([]);
  const [selectedClassParameters, setSelectedClassParameters] = useState<JSClassParameter[]>([]);

  const selectedClassesHaveEditsAvailable =
    components.find(
      (component) => component.className === parseJSClassName(hoverCard.hoveredValues[0] ?? "").parentClassName
    ) !== undefined;

  const shouldShowHoveredComponents = parseJSClassName(selectedClassNames[0] ?? "").group === selectedGroup;
  const hoveredComponents = components.filter(
    (component) =>
      selectedGroup !== "" && selectedClassNames.includes(parseJSClassName(component.className).undotted ?? "none")
  );
  const groupComponents = components.filter(
    (c) =>
      parseJSClassName(c.className).group === selectedGroup &&
      !hoveredComponents.find((h) => h.className === c.className)
  );
  const hasAdvancedParams = useMemo(() => {
    return groupComponents.filter((c) => parseJSClassName(c.className).parameter).length > 0;
  }, [groupComponents]);

  const designSuggestionsForSelectedGroup = designSuggestions.filter((designSuggestion) => {
    if (_.isArray(designSuggestion.groupName)) {
      return designSuggestion.groupName.includes(selectedGroup);
    }
    return designSuggestion.groupName === selectedGroup;
  });
  const [selectedDesignSuggestion, setSelectedDesignSuggestion] = useState<string | null>(null);

  return (
    <AuthenticationWrapper permissionKey={"brandingdesign.customcomponents.interface"}>
      <div>
        <Alert
          severity={"info"}
          style={{ alignItems: "center", transition: "background-color 0.2s", marginBottom: 50, borderRadius: 4 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 30,
            }}
          >
            <p style={{ margin: 0 }}>
              <TranslationWrapper
                tDefault={"Try out the live interface"}
                tKey={"vestiging.tab.customComponents.liveInterface.informationToggle"}
              />
            </p>
            <Button
              onClick={() => {
                setShowLiveInterface(!showLiveInterface);
                setLiveInterfaceIsLoading(true);
                setTimeout(() => {
                  setLiveInterfaceIsLoading(false);
                }, 5000);
              }}
              variant={"outlined"}
            >
              <TranslationWrapper tDefault={"Start live edit mode"} tKey={"live-interface.start-live-edit"} />
            </Button>
          </div>
        </Alert>
        <Modal
          closeAfterTransition
          onClose={() => {
            setShowLiveInterface(!showLiveInterface);
          }}
          open={showLiveInterface}
        >
          <Fade in={showLiveInterface}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "95vh",
                margin: "25px 50px",
                background: "#f7f7f7",
                borderRadius: 4,
                color: "green",
              }}
            >
              <AppBar
                sx={{
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexDirection: "row",
                  padding: 2,
                  mb: 2,
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={() => {
                    setShowLiveInterface(!showLiveInterface);
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <Button
                  color={"success"}
                  onClick={() => {
                    const formElement = document.getElementById("FormV2-submitButton-element");
                    formElement?.click();
                  }}
                  style={{ color: "white", textTransform: "none" }}
                  variant={"contained"}
                >
                  <TranslationWrapper
                    tDefault={"Save changes"}
                    tKey={"vestiging.tab.customComponents.liveInterface.saveText"}
                  />
                </Button>
                {salesareaType !== "KIOSK" ? (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue) {
                          setQrScreenSize(newValue);
                        }
                      }}
                      orientation={"horizontal"}
                      value={qrScreenSize}
                    >
                      <ToggleButton size={"small"} value={"mobile-small"}>
                        <Smartphone fontSize={"small"} />
                        &nbsp;Mobile small
                      </ToggleButton>
                      <ToggleButton size={"small"} value={"mobile-large"}>
                        <Smartphone fontSize={"medium"} />
                        &nbsp;Mobile large
                      </ToggleButton>
                      <ToggleButton size={"small"} value={"tablet"}>
                        <Tablet />
                        &nbsp;Tablet
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue) {
                          setPortraitOrLandscape(newValue);
                        }
                      }}
                      orientation={"horizontal"}
                      value={portraitOrLandscape}
                    >
                      <ToggleButton size={"small"} value={"portrait"}>
                        <Smartphone />
                        &nbsp;
                        <TranslationWrapper tDefault={"Portrait orientation"} tKey={"live-interface.portrait"} />
                      </ToggleButton>
                      <ToggleButton size={"small"} value={"landscape"}>
                        <Smartphone sx={{ transform: "rotate(90deg)" }} />
                        &nbsp;
                        <TranslationWrapper tDefault={"Landscape orientation"} tKey={"live-interface.landscape"} />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </>
                ) : null}
                <div>
                  <ToggleButtonGroup
                    exclusive
                    onChange={(e, v) => {
                      if (v) setEditMode(v);
                    }}
                    orientation={"horizontal"}
                    value={editMode}
                  >
                    <ToggleButton disabled={editMode === "advanced"} size={"small"} value={"simple"}>
                      <EditIcon />
                      &nbsp;
                      <TranslationWrapper tDefault={"Simple mode"} tKey={"live-interface.simple-mode"} />
                    </ToggleButton>
                    <ToggleButton size={"small"} value={"advanced"}>
                      <Settings />
                      &nbsp;
                      <TranslationWrapper tDefault={"Advanced mode"} tKey={"live-interface.advanced-mode"} />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </AppBar>

              <div className={classes.liveInterfaceContent}>
                <div
                  style={{
                    width: 440,
                    display: "flex",
                    flexDirection: "column",
                    gap: 15,
                    flex: salesareaType === "KIOSK" ? undefined : 1,
                  }}
                >
                  <LiveInterface
                    allClasses={components}
                    hoverCard={hoverCard}
                    hoverCardHidden={hoverCardHidden}
                    loadingInterface={liveInterfaceIsLoading}
                    portraitOrLandscape={portraitOrLandscape}
                    qrScreenSize={phoneSizes[qrScreenSize]}
                    salesareaType={salesareaType}
                    selectedClassNamesHaveEditsAvailable={selectedClassesHaveEditsAvailable}
                    setHoverCard={setHoverCard}
                    setHoverCardHidden={setHoverCardHidden}
                    setLoadingInterface={setLiveInterfaceIsLoading}
                    setSelectedGroup={(group) => {
                      setSelectedClassNames(structuredClone(hoverCard.hoveredValues));
                      setSelectedGroup(group);
                      setSelectedClassParameters(structuredClone(hoverCard.hoveredAvailableParameters));
                      if (selectedDesignSuggestion) {
                        setSelectedDesignSuggestion(null);
                      }
                    }}
                  />
                </div>
                <div
                  className={classes.liveInterfaceMaps}
                  onMouseEnter={() => {
                    setHoverCardHidden(true);
                  }}
                  style={{ display: "grid" }}
                >
                  {designSuggestionsForSelectedGroup.length > 0 && editMode === "advanced" ? (
                    <Box sx={{ my: "16px", color: "#3c4858" }}>
                      {selectedDesignSuggestion === null ? (
                        <>
                          <h4>
                            <TranslationWrapper
                              tDefault={"Design suggestions"}
                              tKey={"live-interface.design-suggestions"}
                            />
                          </h4>
                          <ToggleButtonGroup
                            exclusive
                            onChange={(_, newValue) => {
                              if (newValue) setSelectedDesignSuggestion(newValue);
                            }}
                            sx={{
                              margin: "16px",
                            }}
                            value={selectedDesignSuggestion}
                          >
                            {designSuggestionsForSelectedGroup.map((designSuggestion) => (
                              <ToggleButton value={designSuggestion.suggestionName}>
                                <TranslationWrapper
                                  tDefault={"Style as"}
                                  tKey={"live-interface.design-suggestion-name"}
                                />
                                &nbsp;"{designSuggestion.suggestionName}"
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </>
                      ) : null}
                      {selectedDesignSuggestion !== null ? (
                        <DesignSuggestionForm
                          availableParameters={selectedClassParameters}
                          designSuggestions={designSuggestions}
                          onFinish={() => {
                            setSelectedDesignSuggestion(null);
                          }}
                          suggestionName={selectedDesignSuggestion}
                        />
                      ) : (
                        <Divider />
                      )}
                    </Box>
                  ) : null}
                  {selectedDesignSuggestion === null ? (
                    <>
                      <div>
                        {shouldShowHoveredComponents
                          ? hoveredComponents.map((component, idx) => {
                              const translatedLabelName = getTranslation(
                                "CustomComponents." + component.label,
                                "not found"
                              );
                              const translatedLabelComponentCompromised = translatedLabelName
                                .replace(",", ".")
                                .replace(/ /g, "");

                              if (parseJSClassName(component.className).parameter) return null;
                              return (
                                <div key={component.className} style={{ marginLeft: 15, flex: 1 }}>
                                  <JamezzStylesComponent
                                    components={hoveredComponents}
                                    editMode={editMode}
                                    extraProps={{
                                      onMouseEnter: () => {
                                        const classInfo = hoveredComponents[idx].className;
                                        postMessageToIframe("addNewHoveredClass", { classInfo });
                                      },
                                      onMouseLeave: () => {
                                        const classInfo = hoveredComponents[idx].className;
                                        postMessageToIframe("removeNewHoveredClass", { classInfo });
                                      },
                                    }}
                                    index={idx}
                                    showAdminValues={showAdminValues}
                                    startOpened={true}
                                    translatedLabelName={translatedLabelComponentCompromised}
                                  />
                                </div>
                              );
                            })
                          : null}
                        {selectedGroup && groupComponents.length > 0 ? (
                          <>
                            {shouldShowHoveredComponents ? <Divider sx={{ my: 4 }} /> : null}
                            <h4 style={{ color: "#3c4858" }}>
                              <TranslationWrapper
                                tDefault={"Other styles for the same component "}
                                tKey={"live-interface.component"}
                              />
                              <span style={{ fontWeight: "bold" }}>{selectedGroup}</span>
                            </h4>
                          </>
                        ) : null}
                        {groupComponents.map((component, idx) => {
                          const translatedLabelName = getTranslation(
                            "CustomComponents." + component.label,
                            "not found"
                          );
                          const translatedLabelComponentCompromised = translatedLabelName
                            .replace(",", ".")
                            .replace(/ /g, "");

                          if (parseJSClassName(component.className).parameter) return null;
                          return (
                            <div key={component.className} style={{ marginLeft: 15, flex: 1 }}>
                              <JamezzStylesComponent
                                components={groupComponents}
                                editMode={editMode}
                                extraProps={{
                                  onMouseEnter: () => {
                                    const classInfo = groupComponents[idx].className;
                                    postMessageToIframe("addNewHoveredClass", { classInfo });
                                  },
                                  onMouseLeave: () => {
                                    const classInfo = groupComponents[idx].className;
                                    postMessageToIframe("removeNewHoveredClass", { classInfo });
                                  },
                                }}
                                index={idx}
                                showAdminValues={showAdminValues}
                                startOpened={false}
                                translatedLabelName={translatedLabelComponentCompromised}
                              />
                            </div>
                          );
                        })}
                      </div>
                      {hasAdvancedParams ? (
                        <AuthenticationWrapper permissionKey={"idBasedJamezzStyles"}>
                          <>
                            <h1>Advanced styles</h1>
                            <div>
                              {groupComponents.map((component, idx) => {
                                const translatedLabelName = getTranslation(
                                  "CustomComponents." + component.label,
                                  "not found"
                                );
                                const translatedLabelComponentCompromised = translatedLabelName
                                  .replace(",", ".")
                                  .replace(/ /g, "");

                                if (!parseJSClassName(component.className).parameter) return null;

                                return (
                                  <div key={component.className} style={{ marginLeft: 15, flex: 1 }}>
                                    <JamezzStylesComponent
                                      components={groupComponents}
                                      editMode={editMode}
                                      extraProps={{
                                        onMouseEnter: () => {
                                          const classInfo = groupComponents[idx].className;
                                          postMessageToIframe("addNewHoveredClass", { classInfo });
                                        },
                                        onMouseLeave: () => {
                                          const classInfo = groupComponents[idx].className;
                                          postMessageToIframe("removeNewHoveredClass", { classInfo });
                                        },
                                      }}
                                      index={idx}
                                      showAdminValues={showAdminValues}
                                      startOpened={false}
                                      translatedLabelName={translatedLabelComponentCompromised}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        </AuthenticationWrapper>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </Fade>
        </Modal>
      </div>
    </AuthenticationWrapper>
  );
}

type JSClassesHoverMessage = MessageEvent<
  | {
      type: "Highlight-hover";
      content: JsClasses;
      contentDescription: JsClasses;
      contentMousePos: number;
      contentHeight: number;
      contentTopSpacing: number;
      contentLeftSpacing: number;
      contentWidth: number;
      parameters: JSClassParameter[];
    }
  | {
      type: "ElementsHigherInTree";
      content: JsClasses[];
    }
  | {
      type: "LiveInterfaceReady";
    }
>;

function addAnnotatedClassesDynamically(
  classes: JsClasses,
  allClasses: CustomizableComponent[],
  setDynamicallyDefinedComponents: Dispatch<SetStateAction<CustomizableComponent[]>>
): void {
  const componentsWithId = classes.filter((classname) => classname.includes("--"));
  componentsWithId.forEach((c) => {
    const idClassAlreadyAdded = allClasses.find((comp) => comp.className === `.${c}`);
    if (c && !idClassAlreadyAdded) {
      const { parentClassName } = parseJSClassName(c);
      const parentComponent = allClasses.find((comp) => comp.className === parentClassName);
      if (parentComponent) {
        const cloned = structuredClone(parentComponent);
        cloned.className = `.${c}`;
        setDynamicallyDefinedComponents((components) => [...components, cloned]);
      }
    }
  });
}

/*
Live interface class selector
- This is a demo for the live interface class selector
- More information for this component can be found inside the file QrRouter.tsx from line 41
*/
function LiveInterface(props: {
  setSelectedGroup: Dispatch<SetStateAction<string>>;
  loadingInterface: boolean;
  setLoadingInterface: (arg: boolean) => void;
  allClasses: CustomizableComponent[];
  salesareaType: string;
  hoverCard: HoverCardPositions;
  setHoverCard: Dispatch<SetStateAction<HoverCardPositions>>;
  hoverCardHidden: boolean;
  setHoverCardHidden: Dispatch<SetStateAction<boolean>>;
  qrScreenSize: {
    width: number;
    height: number;
  };
  portraitOrLandscape: "portrait" | "landscape";
  selectedClassNamesHaveEditsAvailable: boolean;
}) {
  //Live interface frame styles
  const { classes } = useStyles();
  const iframeref = useRef<HTMLIFrameElement | null>(null);

  const vestigingId = useWatch({ name: "extra_settings__vestiging_id" });

  const [contentDescription, setContentDescription] = useState<string[]>([]);
  const [hoveredJSRoots, setHoveredJSRoots] = useState<JsClasses[]>([]);
  const [interfaceMid, setInterfaceMid] = useState("");
  const [loadingBackgroundColor, setLoadingBackgroundColor] = useState("#F7F7F7");
  const styling = useWatch({ name: "custom_component_styling" });
  const { setDynamicallyDefinedComponents } = useContext(JamezzStylesContext);

  const throttledUpdateStyling = useCallback(
    _.throttle(() => {
      postMessageToIframe("updateStyling", styling);
    }, 750),
    [styling, postMessageToIframe]
  );

  useEffect(() => {
    // preview styling activates preview-only effects that differ from regular appStyling.
    // ie, display: none gets replaced with opacity: 0.5 to allow people to click on them in the live editor
    postMessageToIframe("updateStyling", styling);
  }, []);

  useEffect(throttledUpdateStyling, [styling]);

  //Get value from the iframe with window.onmessage
  useEffect(() => {
    //Get interface mid
    axios
      .get(`firstTableWithoutMastercode/${vestigingId}`)
      .then((response) => {
        setInterfaceMid(response.data.qr_code.mid);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const isKiosk = props.salesareaType === "KIOSK";
  useEffect(() => {
    window.onmessage = function (e: JSClassesHoverMessage) {
      const messageType = e.data.type;
      //If object contains value it will return the value inside a state
      if (messageType === "Highlight-hover") {
        setContentDescription(e.data.contentDescription);
        props.setHoverCardHidden(false);

        // O(n*m) not slow. Measured runtime with performance.now is 0-1ms on my computer
        addAnnotatedClassesDynamically(e.data.content, props.allClasses, setDynamicallyDefinedComponents);

        //Sets all standard values from the iframe
        const iFrameWidth = iframeref.current?.getBoundingClientRect().width ?? 400;
        let orgScreenWidth = iframeref.current?.getBoundingClientRect().width ?? 400;
        let scaleAmount = 1;
        if (isKiosk) {
          orgScreenWidth = 1080;
          scaleAmount = orgScreenWidth / iFrameWidth;
        }
        let hoveredWidth;
        let hoveredLeftSpacing;
        if (e.data.contentLeftSpacing + e.data.contentWidth > orgScreenWidth) {
          const overSpacing = e.data.contentWidth * (e.data.contentWidth / iFrameWidth);
          hoveredWidth = overSpacing;
          hoveredLeftSpacing = iFrameWidth - overSpacing;
        } else {
          hoveredWidth = e.data.contentWidth / scaleAmount;
          hoveredLeftSpacing = e.data.contentLeftSpacing / scaleAmount;
        }
        props.setHoverCard({
          hoveredValues: e.data.content,
          mouseY: e.data.contentMousePos / scaleAmount,
          hoveredWidth,
          hoveredHeight: e.data.contentHeight / scaleAmount,
          hoveredTopSpacing: e.data.contentTopSpacing / scaleAmount,
          hoveredLeftSpacing,
          hoveredAvailableParameters: e.data.parameters,
        });
      } else if (messageType === "ElementsHigherInTree") {
        const elementsHigherInTree = e.data.content;
        for (const els of elementsHigherInTree) {
          addAnnotatedClassesDynamically(els, props.allClasses, setDynamicallyDefinedComponents);
        }
        setHoveredJSRoots(elementsHigherInTree);
      } else if (messageType === "LiveInterfaceReady") {
        postMessageToIframe("init");
        throttledUpdateStyling();
      }
    };

    return () => {
      window.onmessage = null;
    };
  }, [props.allClasses, props.salesareaType, setDynamicallyDefinedComponents]);

  useEffect(() => {
    let timeoutId: number;
    if (props.loadingInterface) {
      timeoutId = window.setTimeout(() => {
        switch (loadingBackgroundColor) {
          case "#F7F7F7":
            setLoadingBackgroundColor("white");
            break;
          case "white":
            setLoadingBackgroundColor("#F7F7F7");
            break;
        }
      }, 1000);

      return () => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
      };
    }
  }, [props.loadingInterface]);

  const isStaging = window.location.href.includes("staging.jamezz.app");
  return (
    <>
      <div
        style={
          props.loadingInterface
            ? { display: "none" }
            : {
                display: "block",
                overflow: "hidden",
              }
        }
      >
        {props.hoverCard.hoveredValues.length ? (
          <HoverCard
            contentDescription={contentDescription}
            hoverCard={props.hoverCard}
            hoverCardHidden={props.hoverCardHidden}
            hoveredRoots={hoveredJSRoots}
            iframeref={iframeref}
            selectedClassNamesHaveEditsAvailable={props.selectedClassNamesHaveEditsAvailable}
            setSelectedGroup={props.setSelectedGroup}
          />
        ) : null}
        <iframe
          className={classes.liveInterfaceIframe}
          id={"liveInterfaceIframe"}
          ref={iframeref}
          src={
            import.meta.env.MODE === "development"
              ? `http://jamezzv2.test/v5/${isKiosk ? "kiosk" : "qr"}/${interfaceMid}`
              : isStaging
                ? `https://staging.jamezz.app/v5/${isKiosk ? "kiosk" : "qr"}/${interfaceMid}`
                : `/dl/${interfaceMid}`
          }
          style={
            isKiosk
              ? {
                  width: 1080,
                  height: 1920,
                  aspectRatio: "9/16",
                  transform: "scale(0.4)",
                  transformOrigin: "top left",
                  border: "9px solid #343434",
                  borderRadius: 20,
                }
              : {
                  width:
                    props.portraitOrLandscape === "portrait" ? props.qrScreenSize.width : props.qrScreenSize.height,
                  height:
                    props.portraitOrLandscape === "portrait" ? props.qrScreenSize.height : props.qrScreenSize.width,
                  border: "3px solid #343434",
                }
          }
        />
      </div>
      {props.loadingInterface ? (
        <div
          className={classes.liveInterfaceLoader}
          style={{
            backgroundColor: loadingBackgroundColor,
            borderRadius: 4,
            transition: "1s",
            width: "100%",
            height: "100%",
          }}
        >
          <CircularProgress color={"primary"} />
        </div>
      ) : null}
    </>
  );
}
